import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { LogOut, Search, Phone, Mail, MapPin, Calendar, Loader2, Inbox } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const PAGE_SIZE = 25;

const AdminDashboardPage = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    axios.get(`${API}/admin/quotes`)
      .then((r) => setLeads(r.data || []))
      .catch((e) => {
        if (e?.response?.status === 401) nav("/admin/login", { replace: true });
        else toast.error("Could not load leads");
      })
      .finally(() => setLoading(false));
  }, [nav]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return leads.filter((l) => {
      if (type !== "all" && l.move_type !== type) return false;
      if (!query) return true;
      return [l.name, l.phone, l.email, l.move_from, l.move_to, l.size]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(query));
    });
  }, [leads, q, type]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSlice = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const logout = async () => {
    try { await axios.post(`${API}/admin/logout`); } catch {}
    nav("/admin/login", { replace: true });
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <section data-testid="admin-dashboard" className="min-h-screen bg-[var(--ct-bg)]">
      <Helmet><meta name="robots" content="noindex, nofollow" /><title>Lead Dashboard — Crosstown Movers</title></Helmet>
      <header className="bg-[var(--ct-primary)] text-white">
        <div className="section-container flex items-center justify-between py-5">
          <div>
            <div className="label-xs text-white">Crosstown Movers</div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Lead Dashboard</h1>
          </div>
          <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-[var(--ct-secondary)]">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </header>

      <div className="section-container py-8">
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ct-text-muted)]" />
            <input data-testid="admin-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Search by name, phone, email, city…"
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-white border border-[var(--ct-border)] focus:ring-2 focus:ring-[var(--ct-primary)] outline-none" />
          </div>
          <select data-testid="admin-filter-type" value={type} onChange={(e) => { setType(e.target.value); setPage(1); }}
            className="px-4 py-3 rounded-xl bg-white border border-[var(--ct-border)] focus:ring-2 focus:ring-[var(--ct-primary)] outline-none">
            <option value="all">All types</option>
            <option value="residential">Residential</option>
            <option value="commercial">Commercial</option>
          </select>
          <div className="text-sm text-[var(--ct-text-muted)] whitespace-nowrap">
            <strong className="text-[var(--ct-primary)]">{filtered.length}</strong> of {leads.length} leads
          </div>
        </div>

        {loading ? (
          <div className="mt-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[var(--ct-primary)]" /></div>
        ) : filtered.length === 0 ? (
          <div className="mt-16 text-center text-[var(--ct-text-muted)]">
            <Inbox className="w-12 h-12 mx-auto mb-4 opacity-40" />
            <p>No leads match your filters.</p>
          </div>
        ) : (
          <>
            <div className="mt-6 bg-white rounded-2xl border border-[var(--ct-border)] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--ct-bg)] text-left text-xs uppercase tracking-widest text-[var(--ct-text-muted)]">
                    <tr>
                      <th className="px-5 py-3">Date</th>
                      <th className="px-5 py-3">Name</th>
                      <th className="px-5 py-3">Phone</th>
                      <th className="px-5 py-3">Type</th>
                      <th className="px-5 py-3">From → To</th>
                      <th className="px-5 py-3">Size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageSlice.map((l) => (
                      <tr key={l.id} data-testid={`lead-row-${l.id}`} onClick={() => setSelected(l)}
                        className="border-t border-[var(--ct-border)] hover:bg-[var(--ct-accent)]/40 cursor-pointer">
                        <td className="px-5 py-4 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                        <td className="px-5 py-4 font-semibold text-[var(--ct-primary)]">{l.name}</td>
                        <td className="px-5 py-4"><a href={`tel:${l.phone}`} className="text-[var(--ct-secondary)] hover:underline">{l.phone}</a></td>
                        <td className="px-5 py-4 capitalize">{l.move_type}</td>
                        <td className="px-5 py-4 text-[var(--ct-text-muted)]">{l.move_from} → {l.move_to}</td>
                        <td className="px-5 py-4 text-[var(--ct-text-muted)]">{l.size || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-center gap-3">
                <button disabled={page === 1} onClick={() => setPage(page - 1)} className="btn-outline text-sm disabled:opacity-30" data-testid="admin-prev">Prev</button>
                <span className="text-sm text-[var(--ct-text-muted)]">Page {page} of {totalPages}</span>
                <button disabled={page === totalPages} onClick={() => setPage(page + 1)} className="btn-outline text-sm disabled:opacity-30" data-testid="admin-next">Next</button>
              </div>
            )}
          </>
        )}
      </div>

      {selected && (
        <div data-testid="lead-detail" className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4" onClick={() => setSelected(null)}>
          <div className="bg-white w-full max-w-lg rounded-3xl p-7 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="label-xs text-[var(--ct-primary)]">Lead Detail</div>
            <h3 className="mt-2 text-2xl font-bold text-[var(--ct-primary)]" style={{ fontFamily: "Outfit" }}>{selected.name}</h3>
            <div className="mt-5 space-y-3 text-sm">
              <Row icon={<Calendar className="w-4 h-4" />} label="Received">{fmtDate(selected.created_at)}</Row>
              <Row icon={<Phone className="w-4 h-4" />} label="Phone"><a className="text-[var(--ct-secondary)] font-semibold" href={`tel:${selected.phone}`}>{selected.phone}</a></Row>
              {selected.email && <Row icon={<Mail className="w-4 h-4" />} label="Email"><a className="text-[var(--ct-secondary)]" href={`mailto:${selected.email}`}>{selected.email}</a></Row>}
              <Row icon={<MapPin className="w-4 h-4" />} label="From → To">{selected.move_from} → {selected.move_to}</Row>
              <Row label="Type" plain>{selected.move_type}</Row>
              {selected.size && <Row label="Size" plain>{selected.size}</Row>}
              {selected.move_date && <Row label="Preferred date" plain>{selected.move_date}</Row>}
              {selected.stairs && <Row label="Stairs/access" plain>{selected.stairs}</Row>}
              {selected.special_items && <Row label="Special items" plain>{selected.special_items}</Row>}
              {selected.packing_needs && <Row label="Packing" plain>{selected.packing_needs}</Row>}
              {selected.message && <Row label="Notes" plain>{selected.message}</Row>}
            </div>
            <button onClick={() => setSelected(null)} className="btn-outline mt-6 w-full text-sm" data-testid="close-detail">Close</button>
          </div>
        </div>
      )}
    </section>
  );
};

const Row = ({ icon, label, children, plain }) => (
  <div className="flex items-start gap-3">
    {icon && <span className="text-[var(--ct-primary)] mt-1">{icon}</span>}
    <div className={plain ? "" : "flex-1"}>
      <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--ct-text-muted)]">{label}</div>
      <div className="text-[var(--ct-text)]">{children}</div>
    </div>
  </div>
);

export default AdminDashboardPage;
